import React from 'react';
import { useApp } from '../contexts/AppContext';
import { Inbox, Check } from 'lucide-react';

export function useL() {
  const { lang } = useApp();
  return { lang, L: (en, bn) => (lang === 'bn' ? bn : en), S: (en, bn) => (lang === 'bn' ? en : bn) };
}

export function PageHeader({ en, bn, subEn, subBn, right }) {
  const { L, S } = useL();
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
      <div>
        <h2 style={{ fontSize: 'var(--text-title2)', fontWeight: 700, letterSpacing: '-0.02em' }}>{L(en, bn)}</h2>
        <p style={{ fontSize: 11, color: 'var(--color-label-tertiary)', marginTop: 2 }}>{S(en, bn)}</p>
        {subEn && <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', marginTop: 8, maxWidth: 720 }}>{L(subEn, subBn)}</p>}
      </div>
      {right && <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{right}</div>}
    </div>
  );
}

export function TabBar({ tabs, tab, setTab, testId = 'tabs' }) {
  const { L, S } = useL();
  return (
    <div data-testid={testId} style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
      {tabs.map(t => {
        const Icon = t.icon; const active = tab === t.id;
        return (
          <button key={t.id} data-testid={`${testId}-${t.id}`} onClick={() => setTab(t.id)} className="btn btn-sm"
            style={{ background: active ? 'var(--color-accent)' : 'var(--color-surface-2)', color: active ? 'white' : 'var(--color-label-secondary)', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px' }}>
            {Icon && <Icon size={15} />}
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
              <span style={{ fontWeight: 500, lineHeight: 1.2 }}>{L(t.en, t.bn)}</span>
              <span style={{ fontSize: 9, opacity: 0.55, lineHeight: 1 }}>{S(t.en, t.bn)}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function StatCard({ icon: Icon, value, en, bn, color = 'var(--color-accent)', suffix, testId }) {
  const { L, S } = useL();
  return (
    <div className="stat-card" data-testid={testId} style={{ textAlign: 'left' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p className="stat-value" style={{ color, fontSize: 32 }}>{value ?? '—'}{suffix && <span style={{ fontSize: 16, opacity: 0.6, marginLeft: 3 }}>{suffix}</span>}</p>
        {Icon && <div style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--color-surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={18} style={{ color }} /></div>}
      </div>
      <div className="stat-label" style={{ marginTop: 6 }}>{L(en, bn)}<span style={{ display: 'block', fontSize: 10, opacity: 0.55 }}>{S(en, bn)}</span></div>
    </div>
  );
}

export function StatGrid({ children, cols = 4 }) {
  return <div className={`grid grid-cols-2 ${cols >= 3 ? 'md:grid-cols-3' : ''} ${cols >= 4 ? 'lg:grid-cols-4' : ''} gap-4`}>{children}</div>;
}

export function Progress({ value = 0, color = 'var(--color-accent)', height = 8, showLabel }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div className="progress-track" style={{ flex: 1, height, background: 'var(--color-surface-2)', borderRadius: height, overflow: 'hidden' }}>
        <div style={{ width: `${v}%`, height: '100%', background: color, borderRadius: height, transition: 'width .6s cubic-bezier(.2,.8,.2,1)' }} />
      </div>
      {showLabel && <span style={{ fontSize: 12, fontWeight: 600, minWidth: 38, textAlign: 'right' }}>{v}%</span>}
    </div>
  );
}

const STATUS_CLASS = {
  ok: 'success', resolved: 'success', operational: 'success', awarded: 'success', placed: 'success', completed: 'success', issued: 'success', recognized: 'success', approved: 'success', disbursed: 'success', in_use: 'success', certified: 'success', 'Compliant': 'success', 'Ready': 'success', 'On Track': 'success', running: 'success', filled: 'success', alive: 'success',
  issue: 'error', out_of_order: 'error', rejected: 'error', 'Not Ready': 'error', 'Action Needed': 'error', critical: 'error', returned: 'error', dead: 'error',
  low_stock: 'warning', pending: 'warning', submitted: 'warning', applied: 'warning', open: 'warning', planned: 'warning', 'Partial': 'warning', 'In Progress': 'warning', 'Behind': 'warning', severe: 'warning', shortlisting: 'warning', expanding: 'warning',
  shortlisted: 'accent', exam: 'accent', interview: 'accent', verified: 'accent', dispatched: 'accent', delivered: 'accent', installed: 'accent', endorsed: 'accent', enrolled: 'accent', nominated: 'accent', finalist: 'accent', moderate: 'accent', active: 'accent', ongoing: 'accent',
};
export function Badge({ status, label, testId }) {
  const cls = STATUS_CLASS[status] || 'neutral';
  return <span data-testid={testId} className={`badge badge-${cls}`} style={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{(label || status || '—').toString().replace(/_/g, ' ')}</span>;
}

export function Row({ children, onClick, testId, style }) {
  return (
    <div className="card-surface" data-testid={testId} onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', padding: 'var(--space-3) var(--space-5)', cursor: onClick ? 'pointer' : 'default', ...style }}>
      {children}
    </div>
  );
}

export function Avatar({ text, color = 'var(--color-accent)', size = 40 }) {
  return <div style={{ width: size, height: size, minWidth: size, borderRadius: '50%', background: 'var(--color-accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.32, fontWeight: 700, color }}>{(text || '?').toString().slice(0, 2).toUpperCase()}</div>;
}

export function RowMain({ title, sub, subBn }) {
  const { L } = useL();
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
      {sub && <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-tertiary)', marginTop: 2 }}>{subBn ? L(sub, subBn) : sub}</div>}
    </div>
  );
}

export function Metric({ value, en, bn, color }) {
  const { L } = useL();
  return (
    <div style={{ textAlign: 'right', minWidth: 64 }}>
      <div style={{ fontSize: 'var(--text-body)', fontWeight: 700, color: color || 'var(--color-label-primary)' }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--color-label-tertiary)' }}>{L(en, bn)}</div>
    </div>
  );
}

export function Field({ en, bn, children, hint }) {
  const { L, S } = useL();
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontSize: 'var(--text-footnote)', fontWeight: 600, marginBottom: 6, color: 'var(--color-label-secondary)' }}>{L(en, bn)} <span style={{ opacity: 0.5, fontWeight: 400 }}>· {S(en, bn)}</span></span>
      {children}
      {hint && <span style={{ display: 'block', fontSize: 11, color: 'var(--color-label-tertiary)', marginTop: 4 }}>{hint}</span>}
    </label>
  );
}

export function Toggle({ en, bn, checked, onChange, testId }) {
  const { L, S } = useL();
  return (
    <button type="button" data-testid={testId} onClick={() => onChange(!checked)}
      style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '12px 14px', borderRadius: 'var(--radius-md)', border: `1.5px solid ${checked ? 'var(--color-accent)' : 'var(--color-separator)'}`, background: checked ? 'var(--color-accent-light)' : 'var(--color-surface-1)', cursor: 'pointer', textAlign: 'left', transition: 'background-color .15s, border-color .15s' }}>
      <span style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${checked ? 'var(--color-accent)' : 'var(--color-label-tertiary)'}`, background: checked ? 'var(--color-accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{checked && <Check size={14} color="white" strokeWidth={3} />}</span>
      <span style={{ flex: 1 }}><span style={{ display: 'block', fontSize: 'var(--text-subhead)', fontWeight: 500 }}>{L(en, bn)}</span><span style={{ display: 'block', fontSize: 10, opacity: 0.5 }}>{S(en, bn)}</span></span>
    </button>
  );
}

export function FormCard({ title, titleBn, children, onSubmit, submitEn = 'Submit', submitBn = 'জমা দিন', busy, testId, maxWidth = 640 }) {
  const { L } = useL();
  return (
    <form data-testid={testId} className="card-surface space-y-4" style={{ maxWidth }} onSubmit={e => { e.preventDefault(); onSubmit?.(); }}>
      {title && <h3 style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{L(title, titleBn)}</h3>}
      {children}
      <button type="submit" data-testid={`${testId}-submit`} disabled={busy} className="btn w-full btn-primary" style={{ opacity: busy ? 0.6 : 1 }}>{L(submitEn, submitBn)}</button>
    </form>
  );
}

export function SectionTitle({ en, bn, right }) {
  const { L, S } = useL();
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
      <h3 style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{L(en, bn)} <span style={{ fontSize: 11, opacity: 0.45, fontWeight: 400, marginLeft: 6 }}>{S(en, bn)}</span></h3>
      {right}
    </div>
  );
}

export function EmptyState({ en = 'Nothing here yet', bn = 'এখনো কিছু নেই' }) {
  const { L } = useL();
  return (
    <div className="card-surface" style={{ textAlign: 'center', padding: 40, color: 'var(--color-label-tertiary)' }}>
      <Inbox size={28} style={{ margin: '0 auto 8px', opacity: 0.5 }} /><div style={{ fontSize: 'var(--text-subhead)' }}>{L(en, bn)}</div>
    </div>
  );
}

export function Stepper({ steps, current }) {
  const { L } = useL();
  const idx = steps.findIndex(s => s.id === current);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {steps.map((s, i) => (
        <React.Fragment key={s.id}>
          <div title={L(s.en, s.bn)} style={{ width: 10, height: 10, borderRadius: '50%', background: i <= idx ? 'var(--color-accent)' : 'var(--color-surface-3)', boxShadow: i === idx ? '0 0 0 3px var(--color-accent-light)' : 'none' }} />
          {i < steps.length - 1 && <div style={{ width: 16, height: 2, background: i < idx ? 'var(--color-accent)' : 'var(--color-surface-3)' }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

export function BarList({ items, color = 'var(--color-accent)', max }) {
  const m = max || Math.max(1, ...items.map(i => i.value));
  return (
    <div className="space-y-2">
      {items.map(i => (
        <div key={i.label} style={{ display: 'grid', gridTemplateColumns: '140px 1fr 48px', alignItems: 'center', gap: 10, fontSize: 'var(--text-footnote)' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--color-label-secondary)' }}>{i.label}</span>
          <Progress value={(i.value / m) * 100} color={i.color || color} height={8} />
          <span style={{ fontWeight: 600, textAlign: 'right' }}>{i.display ?? i.value}</span>
        </div>
      ))}
    </div>
  );
}

export const inputStyle = { width: '100%' };
export const fmt = n => (n ?? 0).toLocaleString('en-IN');
